import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ColorUtil from "@plannel/pages/utils/color-util";
const { snackBarMessageColor } = ColorUtil;

// keyCodes
const KEY_BACKSPACE = 8;
const KEY_DELETE = 46;
const KEY_ENTER = 13;
const KEY_ESC = 27;
const KEY_LEFT_ARROW = 37;
const KEY_RIGHT_ARROW = 39;
const KEY_TAB = 9;

// keyNames
const KEY_NAME_BACKSPACE = "Backspace";
const KEY_NAME_DELETE = "Delete";
const KEY_NAME_ENTER = "Enter";
const KEY_NAME_F2 = "F2";

export default forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { data, eventKey, value } = props;
  const { setSnackInfo, min, max, point, sumFields, sumNotifyFields, maxField, minField } = props;

  let cancelBeforeStart = false;
  if(eventKey !== KEY_NAME_BACKSPACE && eventKey !== KEY_NAME_ENTER && eventKey !== KEY_NAME_F2) {
    cancelBeforeStart = eventKey && '1234567890'.indexOf(eventKey) < 0;
  }
  const originType = typeof value;

  const createInitialState = () => {
    let startValue = 0;
    let highlightFocus = true;

    const isBackspaceOrDelete = eventKey === KEY_NAME_BACKSPACE || eventKey === KEY_NAME_DELETE;
    const isF2OrEnter = eventKey === KEY_NAME_ENTER || eventKey === KEY_NAME_F2;
    let mouseClicked = false;

    if (!eventKey && !eventKey) {
      mouseClicked = true;
    }

    if (!isBackspaceOrDelete && !cancelBeforeStart && !mouseClicked) {
      if (isF2OrEnter) {
        startValue = value;
      } else {
        startValue = eventKey;
        highlightFocus = false;
      }
    }

    if (mouseClicked) {
      startValue = value ? value : startValue;
    }

    return {
      value: startValue,
      name: props.column.colId,
      highlightAllOnFocus: highlightFocus,
    };
  };

  const initialState = createInitialState();
  const [cellValue, setCellValue] = useState(initialState.value);
  const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(initialState.highlightAllOnFocus);
  const editingCellName = initialState.name;
  const refInput = useRef(null);

  const isLeftOrRight = (event) => {
    const { keyCode } = event;
    return [KEY_LEFT_ARROW, KEY_RIGHT_ARROW].indexOf(keyCode) > -1;
  };

  const isCharNumeric = (charStr) => {
    return !isNaN(Number(charStr));
  };

  const isKeyPressedNumeric = (event) => {
    const charStr = event.key;
    return isCharNumeric(charStr);
  };

  const deleteOrBackspace = (event) => {
    const { keyCode } = event;
    return [KEY_DELETE, KEY_BACKSPACE].indexOf(keyCode) > -1;
  };

  const escPressed = (event) => {
    const { keyCode } = event;

    return keyCode === KEY_ESC;
  };

  const ctrlOrShift = (event) => {
    const { ctrlKey, shiftKey } = event;

    return ctrlKey || shiftKey;
  }

  const finishedEditingPressed = (event) => {
    const { keyCode } = event;
    return keyCode === KEY_ENTER || keyCode === KEY_TAB;
  };

  const onKeyDown = (event) => {
    if (isLeftOrRight(event) || deleteOrBackspace(event)) {
      event.stopPropagation();
      return;
    }

    if (!point){
      if (!finishedEditingPressed(event) && !ctrlOrShift(event) && !isKeyPressedNumeric(event)) {
        if (event.preventDefault) event.preventDefault();
      }
    }

    if (escPressed(event)) {
      event.stopPropagation();
      props.stopEditing();
    }

    if (finishedEditingPressed(event)) {
      props.stopEditing();
    }
  };

  const rangeParamCheck = (param) => {
    if(param || param === 0) {
      return param;
    }

    return false;
  };

  const numberRange = (paramValue, paramMin, paramMax) => {
    const inputValue = Number(paramValue);
    const resultObj = {
      isCancel: false,
      snackInfo: {},
      minExist: false,
      maxExist: false,
      pointExist: false
    };

    const minRange = rangeParamCheck(paramMin);
    const maxRange = rangeParamCheck(paramMax);

    resultObj.minExist = minRange || minRange === 0;
    resultObj.maxExist = maxRange || maxRange === 0;
    resultObj.pointExist = point;

    if(resultObj.pointExist){
      if(isNaN(inputValue)){
        resultObj.snackInfo = { open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_NotInNumericFormat")}` };
        resultObj.isCancel = true;
      }
    }

    if (resultObj.minExist && !resultObj.maxExist) {
      if (minRange > inputValue) {
        resultObj.snackInfo = { open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_ValueGreater")} ${minRange}.` };
        resultObj.isCancel = true;
      }
    } else if (!resultObj.minExist && resultObj.maxExist) {
      if (maxRange < inputValue) {
        resultObj.snackInfo = { open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_ValueLess")} ${maxRange}.` };
        resultObj.isCancel = true;
      }
    } else if (resultObj.minExist && resultObj.maxExist) {
      if (minRange > inputValue || maxRange < inputValue) {
        resultObj.snackInfo = { open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_ValueBetween")} ${minRange} - ${maxRange}.` };
        resultObj.isCancel = true;
      }
    }

    return resultObj;
  }

  const sumFieldsRange = (paramValue) => {
    const inputValue = Number(paramValue);
    let resultObj = {
      isCancel: false,
      snackInfo: {},
    };

    let sortFields = [];

    if (sumFields) {
      let sum = inputValue;
      for (const field of sumFields) {
        if(field !== editingCellName){
          sum += Number(data[field]);
        }
        sortFields.push(t(field));
      }

      resultObj = numberRange(sum, min, max);

      if(sumNotifyFields){
        sortFields = [];
        for (const field of sumNotifyFields) {
          sortFields.push(t(field));
        }
      }

      sortFields.sort();

      if (resultObj.isCancel) {
        let content = "";
        if (resultObj.minExist && !resultObj.maxExist) {
          content = `[${sortFields}] ${t("MSG_ValueGreater")} ${min}.`;
        } else if (!resultObj.minExist && resultObj.maxExist) {
          content = `[${sortFields}] ${t("MSG_ValueLess")} ${max}.`;
        } else if (resultObj.minExist && resultObj.maxExist) {
          content = `[${sortFields}] ${t("MSG_ValueBetween")} ${min} - ${max}.`;
        }
        resultObj.snackInfo.content = content;
        return resultObj;
      }
    }

    return resultObj;
  }

  const fieldValueRange = (paramValue) => {
    const inputValue = Number(paramValue);
    
    const maxRange = maxField && data[maxField];
    const minRange = minField && data[minField];
    
    return numberRange(inputValue, minRange, maxRange);
  }

  useImperativeHandle(ref, () => {
    return {
      isCancelBeforeStart() {
        return cancelBeforeStart;
      },

      isCancelAfterEnd() {
        const baseRangeResultObj = numberRange(cellValue, min, max);
        const sumRangeResultObj = sumFieldsRange(cellValue);
        const fieldRangeResultObj = fieldValueRange(cellValue);

        if (baseRangeResultObj.isCancel) {
          if(setSnackInfo){
            setSnackInfo(baseRangeResultObj.snackInfo);
          }
          return true;
        } else if (sumRangeResultObj.isCancel) {
          if(setSnackInfo){
            setSnackInfo(sumRangeResultObj.snackInfo);
          }
          return true;
        } else if (fieldRangeResultObj.isCancel) {
          if(setSnackInfo){
            setSnackInfo(fieldRangeResultObj.snackInfo);
          }  
          return true;
        }

        return false;
      },
    };
  });

  useEffect(() => {
    // DOM이 렌더링된 직후 즉시 focus/select 수행 (setTimeout 제거)
    const eInput = refInput.current;
    
    if (eInput) {
      eInput.focus();
      if (highlightAllOnFocus) {
        eInput.select();
        setHighlightAllOnFocus(false);
      }

      const numValue = Number(initialState.value);
      const finalValue = originType === "string" ? numValue.toString() : numValue;
      props.onValueChange(finalValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightAllOnFocus]);

  return (
    <div>
      <input
        type={"number"}
        ref={refInput}
        className="ag-input-field-input ag-text-field-input"
        value={cellValue}
        onChange={(event) => {
          const returnValue = event.target.value;
          setCellValue(returnValue);
          const numValue = Number(returnValue);
          props.onValueChange(originType === "string" ? numValue.toString() : numValue);
        }}
        onKeyDown={(event) => onKeyDown(event)}
        style={{ width: '100%' }}
      />
    </div>
  );
});
